/**
 * Uses a different fetch api because GED has streaming
 *  axios doesn't handle streaming well
 *  ofetch is like fetch but better
 */

/**
 * @link https://github.com/unjs/ofetch
 */
import { ofetch } from 'ofetch'
import { clone, compose, has, inc, isEmpty, merge, not, path, pathOr, promiseTry, prop, propOr, toString, typeOf } from '~/utils/lib/core/prelude'
import { baseURL, ERRORS } from '~/utils/infra/ged/types'
import httpInterceptor from '~/utils/infra/ged/interceptor'

/** @type {import('./types').RequestConfig} */
export const OFETCH_CONFIG = {
  baseURL,
  timeout: 0, // no timeout handling
  ignoreResponseError: false,
  responseType: 'json',
  retry: 0, // do not use ofetch retry system, use our own system
  retryStatusCodes: [], // do not use ofetch retry system, use our own system
  // custom properties
  withAuthorization: false,
  execCount: 0,
  url: '',
}

/**
 * @constructor
 * @param {import('./types').RequestConstructorParams} _
 * @throws {Error}
 */
const Request = function({
  method,
  route,
  data = undefined,
  params = undefined,
}) {
  /** @type {import('./types').RequestConfig} */
  // @link https://github.com/unjs/ofetch
  this.config = {
    ...OFETCH_CONFIG,
    method,
    body: data,
    params,
    url: route,
  }

  /** @type {import('ofetch').$Fetch} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.handler = undefined

  this.headers = {}

  /** @type {import('./types').RequestResponse} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.response = undefined

  /** @type {import('./types').RequestError} */
  // @ts-ignore: UNSAFE coerce (lazy pattern)
  this.error = undefined

  initialize.call(this)
}

export default Request
/**
 * @function initialize
 * @this {Request}
 * @throws {Error}
 */
function initialize() {
  [
    setHandler,
    addInterceptor,
  ].forEach(init => init.call(this))
}
/**
 * @function accept
 * @param {*} visitor
 * @returns {Promise<void>}
 */
Request.prototype.accept = function(visitor) {
  return promiseTry(() => visitor.visit(this))
}
/**
 *  @function exec
 *  @this {import('~/utils/infra/ged/request').default}
 *  @constructs Request.prototype#
 *  @return {Promise<import('./types').RequestResponse["_data"]>}
 */
Request.prototype.exec = function() {
  this.config.execCount = inc(this.config.execCount)

  // Different pattern then fetch, errors need to be processed after the call
  // hence why the catch here, the interceptor is called after the request has failed
  return this.handler(this.config.url, this.config)
    .then(results => {
      return httpInterceptor.response.call(
        this,
        results
      )
    })
    .catch(err => {

      // @XXX ofetch/fetch uses mutation -> patch: clone useful properties
      const { statusCode, statusMessage, data, request, response } = clone(err);

      return httpInterceptor.responseError.call(
        this,
        {
          statusCode,
          statusMessage,
          data,
          request,
          response
        }
      )
    })
}
/**
 * @function authorize
 * @constructs Request.prototype#
 * @return {Request} chainable build
 */
Request.prototype.authorize = function() {
  this.config.withAuthorization = true
  this.config.credentials = 'include'

  return this
}
/**
 * @function setHeaders
 * @constructs Request.prototype#
 * @param {object} headers
 * @return {Request} chainable build
 */
Request.prototype.setHeaders = function(headers) {
  // @ts-ignore: "ofetch" odd typing (for some reason not the same typing
  // as documented, ie. a simple POJO)
  // @link https://github.com/unjs/ofetch?tab=readme-ov-file#-adding-headers
  this.config.headers = merge(this.config.headers || {}, headers)

  return this
}
/**
 * @function setResponseType
 * @constructs Request.prototype#
 * @param {import('ofetch').ResponseType} responseType
 * @return {Request} chainable build
 */
Request.prototype.setResponseType = function(responseType) {
  this.config.responseType = responseType

  return this
}
/**
 * @function setHandler
 * @this {Request}
 */
function setHandler() {
  // @NOTE: config will actually be assigned during the `exec`
  this.handler = ofetch.create({})
}
/**
 *  @XXX ofetch interceptors
 *
 *  @link https://github.com/unjs/ofetch?tab=readme-ov-file#%EF%B8%8F-interceptors
 *
 *  @function addInterceptor
 *  @this {Request}
 */
function addInterceptor() {
  // @ts-ignore: UNSAFE coerce (ofetch strange typing)
  this.config.onResponse = ({ response }) => this.response = response
}
/**
 * @function requestResponsePrism
 * @param {import('~/utils/infra/ged/types').RequestError} error
 * @throws {Error}
 * @returns {import('~/utils/infra/ged/types').RequestResponse}
 */
export function requestResponsePrism(error) {

  const hasData = pathOr(null, ['data'], error)

  if (!isEmpty(hasData)) return clone(error.data)

  switch (typeOf(error)) {

    case 'object':
      // @ts-ignore: (!) PARTIAL, assuming a matched <object> leads to a
      //                 proper <RequestResponse>
      return clone(error)

    default:
      throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_SCHEMA)
  }
}
/**
 * @function dataPrism
 * @param {import('~/utils/infra/ged/types').RequestResponse} response
 * @throws {Error}
 * @return {any}
 */
export function dataPrism(response) {
  const data = response

  switch (compose(not, isEmpty)(data)) {

    case true:  return clone(data)
    case false: throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_DATA)
  }
}
/**
 * @function messagePrism
 * @desc extracting error informations from <RequestResponse.data>
 *
 *    a) Micro service readable error
 *
 *       {
 *          message: "Some error happened"
 *       }
 *
 *    b) Micro service name fallback
 *
 *      {
 *        name: "ErrorName"
 *      }
 *
 *
 *    c) Micro service error shortcut
 *        {
 *          err: "some error occurred"
 *        }
 *
 *    d) no error provided
 *
 * @param {import('~/utils/infra/ged/types').RequestResponse} response
 * @throws {Error}
 * @return {string}
 */
export function messagePrism(response) {
  const data = dataPrism(response)
  const error = pathOr(data, ['error'], data)

  try {

    // a) Micro service message error
    if (has('message', error)) {
      return toString(
        path(['message'])(error)
      )
    }

    if (typeOf(error) !== 'object')
      throw `not containing <err> object`

    throw `unrecognised <err> pattern matching`

  } catch (_) {
    // b) Micro service error shortcut
    if (typeOf(error) === 'string')
      return error

    // c) no error provided
    throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_MESSAGE)
  }
}
/**
 * @function causePrism
 * @desc extracting error informations from <RequestResponse.data>
 *
 *    a) Micro service error name is provided
 *        {
 *          err:
 *            {
 *              name: "ErrorName"
 *            }
 *        }
 *
 *    b) no error name has been EXPLICITLY provided
 *
 * @param {import('~/utils/infra/ged/types').RequestResponse} response
 * @throws {Error}
 * @return {string}
 */
export function causePrism(response) {
  const data = dataPrism(response)
  const err = propOr(undefined, 'err', data)

  try {

    if (typeOf(err) !== 'object')
      throw `not containing <err> object`

    // a) Micro service error name is provided
    if (has('name', err))
      return compose(toString, prop('name'))(err)

    throw `unrecognised <err> pattern matching`

  } catch (_) {

    // b) no error name has been EXPLICITLY provided
    throw new Error(ERRORS.REQUEST_RESPONSE_UNKNOWN_CAUSE)
  }
}
